import {
  Input,
  OnInit,
  Output,
  Component,
  EventEmitter,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';

import { NgpMoveEvent } from 'ng-primitives/interactions';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { HelpContentService } from '../core/help-content.service';

import { HelpContentState, DimensionValue } from '../core/help.models';

import { Observable } from 'rxjs';

/**
 * Panel content component - draggable help panel (created by portal service at document.body)
 */
@Component({
  selector: 'app-help-panel-content',
  standalone: false,
  templateUrl: './help-panel-content.component.html',
  styleUrl: './help-panel.component.scss',
})
export class HelpPanelContentComponent implements OnInit {
  @ViewChild('panelElement', { static: false })
  panelElement?: ElementRef<HTMLElement>;

  @Input() helpKey!: string;
  @Input() title?: string;

  @Input() width?: DimensionValue;
  @Input() height?: DimensionValue;
  @Input() minWidth?: DimensionValue;
  @Input() minHeight?: DimensionValue;
  @Input() maxWidth?: DimensionValue;
  @Input() maxHeight?: DimensionValue;

  @Input() initialTop?: string;
  @Input() initialLeft?: string;

  @Output() closed = new EventEmitter<void>();

  contentState$!: Observable<HelpContentState>;

  faExclamationTriangle = faExclamationTriangle;

  titleId = `help-panel-title-${Math.random().toString(36).substr(2, 9)}`;
  displayTitle: string = '';

  private currentLeft = 100;
  private currentTop = 100;

  constructor(
    private helpContentService: HelpContentService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    if (!this.helpKey) {
      console.warn('HelpPanelContentComponent: helpKey is required');
      return;
    }

    // * Set title - use provided or translate default
    this.displayTitle = this.title || this.translate.instant('help.default-title');

    this.contentState$ = this.helpContentService.getContent(this.helpKey);

    if (this.initialLeft) {
      this.currentLeft = parseFloat(this.initialLeft);
    }
    if (this.initialTop) {
      this.currentTop = parseFloat(this.initialTop);
    }

    // * clamp initial position to Viewport
    setTimeout(() => this.clampPosition(), 0);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.clampPosition();
  }

  close(): void {
    this.closed.emit();
  }

  onMove(event: NgpMoveEvent): void {
    if (!this.panelElement) {
      return;
    }

    let newLeft = this.currentLeft + event.deltaX;
    let newTop = this.currentTop + event.deltaY;

    const panel = this.panelElement.nativeElement;
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    // * clamp to Viewport boundaries
    const maxLeft = window.innerWidth - panelWidth;
    const maxTop = window.innerHeight - panelHeight;

    this.currentLeft = Math.max(0, Math.min(newLeft, maxLeft));
    this.currentTop = Math.max(0, Math.min(newTop, maxTop));
  }

  get customStyles(): Record<string, string> {
    const styles: Record<string, string> = {};

    // Position (updated during drag)
    styles['left'] = `${this.currentLeft}px`;
    styles['top'] = `${this.currentTop}px`;

    if (this.width) {
      styles['width'] = this.resolveDimension(this.width);
    }
    if (this.height) {
      styles['height'] = this.resolveDimension(this.height);
    }
    if (this.minWidth) {
      styles['min-width'] = this.resolveDimension(this.minWidth);
    }
    if (this.minHeight) {
      styles['min-height'] = this.resolveDimension(this.minHeight);
    }
    if (this.maxWidth) {
      styles['max-width'] = this.resolveDimension(this.maxWidth);
    }
    if (this.maxHeight) {
      styles['max-height'] = this.resolveDimension(this.maxHeight);
    }

    return styles;
  }

  private resolveDimension(value: DimensionValue): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  // * clamp Panel position to Viewport boundaries
  private clampPosition(): void {
    if (!this.panelElement) {
      return;
    }

    const panel = this.panelElement.nativeElement;
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    const maxLeft = window.innerWidth - panelWidth;
    const maxTop = window.innerHeight - panelHeight;

    this.currentLeft = Math.max(0, Math.min(this.currentLeft, maxLeft));
    this.currentTop = Math.max(0, Math.min(this.currentTop, maxTop));
  }
}
